import { ManageAccountsOutlined, SearchOutlined } from '@mui/icons-material';
import {
    Box,
    DialogContent,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { type FC, useMemo, useState } from 'react';

import Result from 'components/v2/common/Result';
import DefaultConsultantSelect from 'views/v2/adminPanel/components/defaultConsultantSelect';

import useTranslate from 'hooks/useTranslate';
import type {
    ServiceConsultant,
    ServiceConsultantCountryCode,
    ServiceConsultantProgram,
} from 'types';
import {
    getLocalizedServiceName,
    getServiceConsultantName,
} from 'views/v2/adminPanel/helpers/serviceConsultants';

interface ServiceDefaultConsultantAssignmentsProps {
    defaultConsultant: ServiceConsultant | null;
    services: ServiceConsultantProgram[];
    updatingServiceIds: ReadonlySet<number>;
    onAssignmentChange: (
        service: ServiceConsultantProgram,
        consultantId: number | null
    ) => void | Promise<void>;
}

const COUNTRY_OPTIONS: Array<{
    code: ServiceConsultantCountryCode;
    labelKey: string;
}> = [
    { code: 'AU', labelKey: 'australia' },
    { code: 'CA', labelKey: 'canada' },
    { code: 'UK', labelKey: 'unitedKingdom' },
];

export const ServiceDefaultConsultantAssignments: FC<ServiceDefaultConsultantAssignmentsProps> = ({
    defaultConsultant,
    services,
    updatingServiceIds,
    onAssignmentChange,
}) => {
    const { language, translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [selectedCountry, setSelectedCountry] = useState<ServiceConsultantCountryCode>('AU');
    const [search, setSearch] = useState('');

    const allCountryServices = useMemo(
        () => services.filter((service) => service.country_code === selectedCountry),
        [selectedCountry, services]
    );
    const visibleServices = useMemo(() => {
        const query = search.trim().toLowerCase();
        return allCountryServices
            .filter(
                (service) =>
                    !query ||
                    getLocalizedServiceName(service, language).toLowerCase().includes(query) ||
                    service.service_code.toLowerCase().includes(query)
            )
            .sort((left, right) =>
                getLocalizedServiceName(left, language).localeCompare(
                    getLocalizedServiceName(right, language)
                )
            );
    }, [allCountryServices, language, search]);
    const customizedCount = allCountryServices.filter(
        (service) => service.consultant_id != null
    ).length;

    const consultantSelect = (service: ServiceConsultantProgram) => {
        const isUpdating = updatingServiceIds.has(service.service_id);

        return (
            <DefaultConsultantSelect
                companyDefaultConsultant={defaultConsultant}
                consultants={service.eligible_consultants}
                disabled={isUpdating}
                loading={isUpdating}
                serviceName={getLocalizedServiceName(service, language)}
                value={service.consultant_id}
                onChange={(consultantId) => onAssignmentChange(service, consultantId)}
            />
        );
    };

    const serviceDetails = (service: ServiceConsultantProgram) => {
        const serviceName = getLocalizedServiceName(service, language);

        return (
            <Typography variant="body2" noWrap title={serviceName} sx={{ fontWeight: 600 }}>
                {serviceName}
            </Typography>
        );
    };

    return (
        <DialogContent
            dividers
            sx={{
                p: 0,
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                minHeight: { xs: 0, sm: 520 },
                overflow: 'hidden',
                bgcolor: 'rgba(248, 250, 252, 0.55)',
            }}
        >
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    alignItems: { xs: 'stretch', md: 'center' },
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                }}
            >
                <FormControl size="small" sx={{ width: { xs: '100%', md: 190 } }}>
                    <InputLabel id="default-consultant-country-label">
                        {translate('country')}
                    </InputLabel>
                    <Select
                        labelId="default-consultant-country-label"
                        label={translate('country')}
                        value={selectedCountry}
                        onChange={(event) =>
                            setSelectedCountry(event.target.value as ServiceConsultantCountryCode)
                        }
                    >
                        {COUNTRY_OPTIONS.map((country) => (
                            <MenuItem key={country.code} value={country.code}>
                                {translate(country.labelKey)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    label={translate('searchPrograms')}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    sx={{ width: { xs: '100%', md: 320 } }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlined sx={{ fontSize: 18 }} />
                                </InputAdornment>
                            ),
                        },
                    }}
                />
                <Box sx={{ flex: 1 }} />
                <Stack spacing={0.25} sx={{ alignItems: { xs: 'flex-start', md: 'flex-end' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {customizedCount} / {allCountryServices.length}{' '}
                        {translate('programsCustomized')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {translate('companyDefault')}:{' '}
                        {defaultConsultant
                            ? getServiceConsultantName(defaultConsultant)
                            : translate('notConfigured')}
                    </Typography>
                </Stack>
            </Stack>

            {!visibleServices.length ? (
                <Box sx={{ p: { xs: 2, sm: 3 }, flex: 1, overflow: 'auto' }}>
                    <Result
                        icon={<ManageAccountsOutlined sx={{ fontSize: 32 }} />}
                        title={translate(
                            allCountryServices.length
                                ? 'noProgramsMatchSearch'
                                : 'noProgramsAvailable'
                        )}
                        description={translate(
                            allCountryServices.length
                                ? 'tryAdjustingSearch'
                                : 'noProgramsForCountry'
                        )}
                    />
                </Box>
            ) : isMobile ? (
                <Stack spacing={1.25} sx={{ p: 2, flex: 1, overflow: 'auto' }}>
                    {visibleServices.map((service) => (
                        <Paper
                            key={service.service_id}
                            variant="outlined"
                            sx={{ p: 1.25, borderRadius: 2 }}
                        >
                            <Stack spacing={0.75}>
                                {serviceDetails(service)}
                                <Stack spacing={0.25}>
                                    <Typography variant="caption" color="text.secondary">
                                        {translate('defaultConsultant')}
                                    </Typography>
                                    {consultantSelect(service)}
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            ) : (
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                    <Table
                        stickyHeader
                        size="small"
                        sx={{
                            tableLayout: 'fixed',
                            '& .MuiTableCell-root': {
                                px: 2,
                                py: 0.5,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    {translate('program')}
                                </TableCell>
                                <TableCell sx={{ width: 380, fontWeight: 700 }}>
                                    {translate('defaultConsultant')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleServices.map((service) => (
                                <TableRow key={service.service_id} hover>
                                    <TableCell>{serviceDetails(service)}</TableCell>
                                    <TableCell>{consultantSelect(service)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </DialogContent>
    );
};

export default ServiceDefaultConsultantAssignments;
